import React from 'react';
import { Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

/**
 * The one place dive-safety wording lives. Any surface that shows dive-safety content (conditions,
 * site reference info, first-aid or training material, computed gas figures) gets the matching note
 * from here rather than its own copy, so the warnings stay consistent and are easy to audit. Anything
 * that shows Nereus's words uses NereusCaution instead.
 */
export const SAFETY_NOTES = {
  /** A single conditions report. The hub summary card words the same idea in the plural. */
  conditions: "Conditions change fast. This is one diver's opinion, not a dive briefing.",
  /** Catalog facts about a site (type, difficulty, region, location). */
  siteReference: 'Site details are general reference only. Access, conditions and hazards change — check locally with a dive operator before you go.',
  /** Flashcard decks, including the first-aid and rescue material. */
  studyAid: 'These cards are a study aid, not medical advice and not a substitute for a certified first-aid, oxygen-provider or rescue course.',
  /** Gas consumption shown on a dive. */
  gasEstimate: 'Gas figures are calculated or estimated from the data available — not for gas planning.',
  /** Breathing drills. */
  breathwork: 'Stop if you feel lightheaded. Never practise breath-holding in or near water or while driving, and check with your doctor first if you have a heart, lung or blood-pressure condition.',
} as const;

export type SafetyNoteKind = keyof typeof SAFETY_NOTES;

interface SafetyNoteProps {
  kind: SafetyNoteKind;
  sx?: SxProps<Theme>;
}

const SafetyNote: React.FC<SafetyNoteProps> = ({ kind, sx }) => (
  <Typography
    role="note"
    variant="caption"
    color="text.secondary"
    sx={[{ display: 'block', fontStyle: 'italic' }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
  >
    {SAFETY_NOTES[kind]}
  </Typography>
);

export default SafetyNote;
